import React, { useState, useCallback } from 'react';
import { ChevronDown, ChevronRight } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';

export const SeverityBadge = ({ severity }) => {
  const config = {
    critical: { variant: 'danger', label: 'Critical' },
    high: { variant: 'suspicious', label: 'High' },
    medium: { variant: 'suspicious', label: 'Medium' },
    low: { variant: 'secondary', label: 'Low' },
    safe: { variant: 'safe', label: 'Safe' },
    info: { variant: 'secondary', label: 'Info' },
  };
  const c = config[severity] || config.info;
  return <Badge variant={c.variant} className="text-[10px]">{c.label}</Badge>;
};

export const DangerBadge = ({ level }) => {
  const config = {
    low: { color: 'bg-warning/20 text-warning border-warning/30', label: 'Low' },
    medium: { color: 'bg-orange-500/20 text-orange-600 border-orange-500/30', label: 'Medium' },
    high: { color: 'bg-destructive/20 text-destructive border-destructive/30', label: 'High' },
    critical: { color: 'bg-destructive/30 text-destructive border-destructive/40', label: 'Critical' },
  };
  const c = config[level] || config.medium;
  return <span className={cn("px-2 py-0.5 rounded-md text-[10px] font-semibold border", c.color)}>{c.label}</span>;
};

export const TabButton = ({ active, onClick, icon: Icon, label, testId }) => (
  <button onClick={onClick} data-testid={testId}
    className={cn(
      "flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-medium transition-all",
      active ? "bg-primary text-primary-foreground shadow-md"
             : "bg-muted/50 hover:bg-muted text-muted-foreground hover:text-foreground"
    )}>
    <Icon className="w-4 h-4" />
    <span className="hidden sm:inline">{label}</span>
  </button>
);

// Iterative tree renderer to avoid recursive JSX (babel plugin limitation)
const severityColors = {
  critical: 'border-l-destructive', high: 'border-l-orange-500',
  medium: 'border-l-warning', low: 'border-l-blue-400',
  safe: 'border-l-success', info: 'border-l-muted-foreground',
};
const severityBg = {
  critical: 'bg-destructive/5', high: 'bg-orange-500/5',
  medium: 'bg-warning/5', safe: 'bg-success/5',
};

const SingleNode = ({ node, depth, isExpanded, onToggle }) => {
  const hasChildren = node.children && node.children.length > 0;
  return (
    <div style={{ paddingLeft: depth * 16 }}
      className={cn("border-l-2 ml-1", severityColors[node.severity] || 'border-l-border')}>
      <div
        className={cn(
          "rounded-lg p-2.5 sm:p-3 mb-1.5 transition-all",
          severityBg[node.severity] || 'bg-muted/30',
          hasChildren && "cursor-pointer hover:bg-muted/50"
        )}
        onClick={() => hasChildren && onToggle(node.id)}
        data-testid={`risk-node-${node.id}`}
      >
        <div className="flex items-start gap-2">
          {hasChildren ? (
            isExpanded ? <ChevronDown className="w-4 h-4 mt-0.5 flex-shrink-0 text-muted-foreground" />
                       : <ChevronRight className="w-4 h-4 mt-0.5 flex-shrink-0 text-muted-foreground" />
          ) : <div className="w-4" />}
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-xs sm:text-sm font-medium">{node.name}</span>
              <SeverityBadge severity={node.severity} />
              {node.score > 0 && <span className="text-[10px] font-mono text-destructive">+{node.score} risk</span>}
            </div>
            <p className="text-[10px] sm:text-xs text-muted-foreground mt-0.5">{node.description}</p>
          </div>
        </div>
      </div>
    </div>
  );
};

// Flatten tree into renderable list based on expansion state
function flattenTree(nodes, expandedSet, depth = 0) {
  const flat = [];
  for (const node of nodes) {
    flat.push({ ...node, depth });
    const isExpanded = expandedSet.has(node.id);
    if (isExpanded && node.children && node.children.length > 0) {
      flat.push(...flattenTree(node.children, expandedSet, depth + 1));
    }
  }
  return flat;
}

export const RiskGraph = ({ signals }) => {
  const [expanded, setExpanded] = useState(() => {
    // Auto-expand root nodes
    const set = new Set();
    signals.forEach(s => set.add(s.id));
    return set;
  });

  const toggle = useCallback((id) => {
    setExpanded(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const flatNodes = flattenTree(signals, expanded);

  return (
    <div className="space-y-0.5" data-testid="risk-graph">
      {flatNodes.map(node => (
        <SingleNode key={node.id} node={node} depth={node.depth}
          isExpanded={expanded.has(node.id)} onToggle={toggle} />
      ))}
    </div>
  );
};
